import { atom } from 'jotai'

import { Currency, CurrencyAmount } from '@cowprotocol/currency'

import { TradeAmounts } from 'common/types'

interface TradeConfirmModalState {
  sessionId: number
  isOpen: boolean
  pendingTrade: TradeAmounts | null
  transactionHash: string | null
  error: string | null
  permitSignatureState: undefined | 'requested' | 'signed'
  permitAmount: CurrencyAmount<Currency> | null
  /**
   * When true, the user will be forced to confirm the price before placing an order
   * The confirmation is happening through "Price updated" banner
   */
  forcePriceConfirmation: boolean
  /**
   * True from the moment the user clicks the confirm button until the trade flow resolves,
   * fails, or is dismissed. Used to freeze the quote-derived amounts shown in the confirm modal
   * so they never change to a different value than what the user actually confirmed/signed.
   */
  isConfirming: boolean
}

export const tradeConfirmStateAtom = atom<TradeConfirmModalState>({
  sessionId: 0,
  isOpen: false,
  pendingTrade: null,
  transactionHash: null,
  error: null,
  permitSignatureState: undefined,
  permitAmount: null,
  forcePriceConfirmation: false,
  isConfirming: false,
})

export const setOpenTradeConfirmAtom = atom(null, (get, set, forcePriceConfirmation: boolean = false) => {
  set(tradeConfirmStateAtom, () => ({
    sessionId: get(tradeConfirmStateAtom).sessionId + 1,
    isOpen: true,
    error: null,
    pendingTrade: null,
    transactionHash: null,
    permitSignatureState: undefined,
    permitAmount: null,
    forcePriceConfirmation,
    isConfirming: false,
  }))
})

export const setCloseTradeConfirmAtom = atom(null, (get, set) => {
  set(tradeConfirmStateAtom, () => ({
    ...get(tradeConfirmStateAtom),
    sessionId: get(tradeConfirmStateAtom).sessionId + 1,
    isOpen: false,
    error: null,
    pendingTrade: null,
    permitSignatureState: undefined,
    permitAmount: null,
    forcePriceConfirmation: false,
    isConfirming: false,
  }))
})

export const setErrorTradeConfirmAtom = atom(null, (get, set, error: string) => {
  set(tradeConfirmStateAtom, () => ({
    ...get(tradeConfirmStateAtom),
    sessionId: get(tradeConfirmStateAtom).sessionId + 1,
    error,
    isOpen: false,
    pendingTrade: null,
    transactionHash: null,
    permitSignatureState: undefined,
    permitAmount: null,
    forcePriceConfirmation: false,
    isConfirming: false,
  }))
})

export const setConfirmingTradeConfirmAtom = atom(
  null,
  (get, set, params: { isConfirming: boolean; sessionId: number }) => {
    if (get(tradeConfirmStateAtom).sessionId !== params.sessionId) return

    set(tradeConfirmStateAtom, () => ({
      ...get(tradeConfirmStateAtom),
      isConfirming: params.isConfirming,
    }))
  },
)

export const setPermitSignatureRequestedTradeConfirmAtom = atom(
  null,
  (get, set, pendingTrade: TradeAmounts, permitAmount?: CurrencyAmount<Currency>) => {
    set(tradeConfirmStateAtom, () => ({
      ...get(tradeConfirmStateAtom),
      pendingTrade,
      permitSignatureState: 'requested',
      permitAmount: permitAmount ?? null,
    }))
  },
)

export const setPendingTradeConfirmAtom = atom(null, (get, set, pendingTrade: TradeAmounts) => {
  const currentState = get(tradeConfirmStateAtom)

  set(tradeConfirmStateAtom, () => {
    return {
      ...currentState,
      error: null,
      pendingTrade,
      transactionHash: null,
      // Only move to the next state if coming in the right sequence.
      // Otherwise, reset it
      permitSignatureState: currentState.permitSignatureState === 'requested' ? 'signed' : undefined,
    }
  })
})

export const setTxHashTradeConfirmAtom = atom(null, (get, set, transactionHash: string) => {
  set(tradeConfirmStateAtom, () => ({
    ...get(tradeConfirmStateAtom),
    error: null,
    pendingTrade: null,
    transactionHash,
  }))
})
